/**
 * The app API client: the only place the browser calls `fetch`.
 *
 * Every path is resolved beneath `/api`, so no code path can aim a request at
 * a FHIR server or an authorisation server; all of that traffic goes through
 * the backend proxy (Principle I). Failures arrive as a single error shape
 * carrying plain-language copy, and a `401` outside the auth routes raises a
 * signal the shell turns into a sign-in redirect.
 *
 * @author John Grimes
 */

import { toPlainError } from "../fhir/operationOutcome";

import type { PlainError } from "shared/api";

/** Prefix every app API path is resolved beneath. */
export const API_BASE_PATH = "/api";

/** Methods the app API uses. */
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Options for a single app API request. */
export type RequestOptions = {
  /** HTTP method; defaults to `GET`. */
  method?: HttpMethod;
  /** Value serialised as the JSON request body, when there is one. */
  body?: unknown;
  /** Extra request headers; these win over the defaults. */
  headers?: Record<string, string>;
  /** Signal used to cancel the request, for previews and exports. */
  signal?: AbortSignal;
};

/**
 * The error every app API call throws. `plain` is what the user sees; `status`
 * is absent when the request never reached the server; `unauthorised` marks the
 * failures the shell answers with a sign-in redirect; `needsReauth` marks the
 * one `401` that is not about the app session at all, but about a connection
 * whose authorisation with its FHIR server can no longer be renewed (FR-014).
 */
export type ApiError = Error & {
  readonly name: "ApiError";
  readonly status?: number;
  readonly plain: PlainError;
  readonly unauthorised: boolean;
  readonly needsReauth: boolean;
};

/** Notified when a request failed because the app session is not valid. */
export type UnauthorisedListener = () => void;

/** JSON media type used for request and response negotiation. */
const JSON_MEDIA_TYPE = "application/json";

/** Status that means the app session is missing or expired. */
const UNAUTHORISED_STATUS = 401;

/**
 * Paths whose `401` means "those credentials were wrong", not "you are signed
 * out", so they must not trigger a redirect to sign-in.
 */
const AUTH_PATH_PREFIX = "/auth/";

/**
 * The `reason` the FHIR proxy sends with a `401` when a connection's
 * authorisation could not be renewed (contracts/app-api.md). The account's own
 * session is intact, so this one must not send the user to sign-in.
 */
const NEEDS_REAUTH_REASON = "needs_reauth";

/** Live unauthorised subscribers, added and removed as the shell mounts. */
const unauthorisedListeners = new Set<UnauthorisedListener>();

/**
 * Registers a listener for unauthorised responses, so the shell can send the
 * user to sign-in while preserving where they were.
 *
 * @param listener - Called once per unauthorised response.
 * @returns A function that removes the listener.
 *
 * @example
 * ```ts
 * useEffect(() => subscribeToUnauthorised(() => navigate("/sign-in")), []);
 * ```
 */
export const subscribeToUnauthorised = (
  listener: UnauthorisedListener,
): (() => void) => {
  unauthorisedListeners.add(listener);
  return () => {
    unauthorisedListeners.delete(listener);
  };
};

/**
 * Narrows a thrown value to the client's own error type.
 *
 * @param value - The value to test.
 * @returns True when the value is an `ApiError`.
 */
export const isApiError = (value: unknown): value is ApiError =>
  value instanceof Error && value.name === "ApiError" && "plain" in value;

/**
 * What a failure means for the shell. The three are mutually exclusive: a
 * `401` is either the account's session or one connection's authorisation,
 * never both, so the two flags on the error are derived from one value rather
 * than set independently.
 */
type FailureKind = "ordinary" | "unauthorised" | "needsReauth";

/**
 * Builds the error thrown for every failure, so callers only ever handle one
 * shape. Exported for the failures a caller recognises itself, such as an
 * assistant request that could not be resumed.
 *
 * @param plain - The plain-language message and technical detail.
 * @param status - The HTTP status, absent for a transport failure.
 * @param kind - What the failure means for the shell.
 * @returns The error to throw.
 *
 * @example
 * ```ts
 * throw createApiError({ message: "It could not be resumed.", detail });
 * ```
 */
export const createApiError = (
  plain: PlainError,
  status?: number,
  kind: FailureKind = "ordinary",
): ApiError =>
  Object.assign(new Error(plain.message), {
    name: "ApiError" as const,
    status,
    plain,
    unauthorised: kind === "unauthorised",
    needsReauth: kind === "needsReauth",
  });

/**
 * Interprets a response body: JSON when it parses, otherwise raw text. The two
 * fields feed straight into the plain-language mapping.
 *
 * @param text - The response body as text.
 * @returns The parsed body, the raw text, or neither for an empty body.
 */
const readBody = (text: string): { body?: unknown; text?: string } => {
  if (text.trim() === "") {
    return {};
  }
  try {
    return { body: JSON.parse(text) as unknown };
  } catch {
    return { text };
  }
};

/**
 * Issues the request. Transport failures become an `ApiError` with no status;
 * a cancellation is rethrown untouched so callers can tell the two apart.
 *
 * @param path - Path beneath the API prefix, starting with a slash.
 * @param options - Method, body, headers and signal.
 * @param accept - Media type to accept, when the caller wants one.
 * @returns The response, whatever its status.
 * @throws {ApiError} For a transport failure. An abort is rethrown as it was.
 */
const performRequest = async (
  path: string,
  options: RequestOptions,
  accept?: string,
): Promise<Response> => {
  const { method = "GET", body, headers, signal } = options;
  const init: RequestInit = {
    method,
    // The session cookie is httpOnly and same-origin; nothing else authorises
    // an app API call.
    credentials: "same-origin",
    headers: {
      ...(accept === undefined ? {} : { Accept: accept }),
      ...(body === undefined ? {} : { "Content-Type": JSON_MEDIA_TYPE }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  };
  try {
    return await fetch(`${API_BASE_PATH}${path}`, init);
  } catch (error) {
    // An abort is the caller's own doing, so it must stay recognisable.
    if (
      typeof error === "object" &&
      error !== null &&
      "name" in error &&
      error.name === "AbortError"
    ) {
      throw error;
    }
    throw createApiError(toPlainError({ cause: error }));
  }
};

/**
 * Turns a non-2xx response into the error to throw, raising the unauthorised
 * signal on the way when the shell needs to react.
 *
 * @param response - The failed response; its body is consumed here.
 * @param path - Path beneath the API prefix, used to spot the auth routes.
 * @returns The error to throw.
 */
const toFailure = async (
  response: Response,
  path: string,
): Promise<ApiError> => {
  const text = await response.text().catch(() => "");
  const parsed = readBody(text);
  const plain = toPlainError({
    status: response.status,
    statusText: response.statusText,
    ...parsed,
  });
  if (response.status !== UNAUTHORISED_STATUS) {
    return createApiError(plain, response.status);
  }
  // A connection's lapsed authorisation is the caller's problem to offer a
  // remedy for; the account's own session is untouched (FR-014).
  const body = parsed.body;
  if (
    typeof body === "object" &&
    body !== null &&
    "reason" in body &&
    body.reason === NEEDS_REAUTH_REASON
  ) {
    return createApiError(plain, response.status, "needsReauth");
  }
  // A refused sign-in is a message on the sign-in screen, not a redirect loop.
  if (path.startsWith(AUTH_PATH_PREFIX)) {
    return createApiError(plain, response.status);
  }
  for (const listener of unauthorisedListeners) {
    listener();
  }
  return createApiError(plain, response.status, "unauthorised");
};

/**
 * Calls the app API and returns the parsed JSON body.
 *
 * @param path - Path beneath the API prefix, starting with a slash.
 * @param options - Method, body, headers and signal.
 * @returns The parsed response body.
 * @throws {ApiError} When the response is not a 2xx, or is not JSON.
 *
 * @example
 * ```ts
 * const me = await apiJson<Me>("/me");
 * ```
 */
export const apiJson = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> => {
  const response = await performRequest(path, options, JSON_MEDIA_TYPE);
  if (!response.ok) {
    throw await toFailure(response, path);
  }
  const parsed = readBody(await response.text());
  if (!("body" in parsed)) {
    // A 2xx that is not JSON is a broken server, not a successful call.
    throw createApiError(
      toPlainError({
        status: response.status,
        statusText: response.statusText,
        ...parsed,
      }),
      response.status,
    );
  }
  return parsed.body as T;
};

/**
 * Calls the app API where a successful response carries no body.
 *
 * @param path - Path beneath the API prefix, starting with a slash.
 * @param options - Method, body, headers and signal.
 * @returns Nothing.
 * @throws {ApiError} When the response is not a 2xx.
 *
 * @example
 * ```ts
 * await apiVoid("/auth/logout", { method: "POST" });
 * ```
 */
export const apiVoid = async (
  path: string,
  options: RequestOptions = {},
): Promise<void> => {
  const response = await performRequest(path, options, JSON_MEDIA_TYPE);
  if (!response.ok) {
    throw await toFailure(response, path);
  }
};

/**
 * Calls the app API and hands back the response with its body untouched, for
 * callers that stream it themselves (export downloads, FR-053).
 *
 * @param path - Path beneath the API prefix, starting with a slash.
 * @param options - Method, body, headers and signal.
 * @returns The response, with its body unread.
 * @throws {ApiError} When the response is not a 2xx.
 *
 * @example
 * ```ts
 * const response = await apiStream(`/connections/${id}/fhir/$sql-run`, {
 *   method: "POST",
 *   body: parameters,
 *   signal: controller.signal,
 * });
 * const reader = response.body?.getReader();
 * ```
 */
export const apiStream = async (
  path: string,
  options: RequestOptions = {},
): Promise<Response> => {
  const response = await performRequest(path, options);
  if (!response.ok) {
    throw await toFailure(response, path);
  }
  return response;
};
